import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecords, Empty, Err, ago } from '../ui.js';
import { createRecord, ApiError } from '../api.js';
import { AgentRun } from './AgentRun.js';
import type { DiscoveredAccount, IcpProfile, Persona } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// RESEARCH — reworked twice, and the second pass killed most of the first.
//
// V1 was four numbered sections — kind, source, target, run — with a four-step
// instruction strip above them explaining how to use it. That strip was the
// tell: I had written instructions for a confusing screen instead of fixing the
// screen. It also promised four kinds of research when three of them produced
// the same output, which is a page making a promise it cannot keep.
//
// V2 was going to be a console with mode chips, because that is what worked on
// the account record. Two objections killed it, and both are worth writing down.
//
//   A PATTERN APPLIED EVERYWHERE IS A CRUTCH. The console fits the account
//   record because a record supports several intents against one subject. This
//   page has one subject and almost one verb. Wrapping that in mode chips would
//   have been consistency for its own sake, and it would have implied optionality
//   that does not exist.
//
//   POLISHING CHROME OVER A MISSING FUNCTION IS WORSE THAN AN UGLY PAGE. At the
//   time there was no search behind this at all. A prettier form would have hidden
//   that better, and hiding it is exactly the wrong outcome — the hole gets found
//   later, with less trust in everything near it. So the page said so plainly.
//
//   THAT GAP IS NOW CLOSED. The research agent (agent-core.ts) searches the web
//   server-side and stages what it finds. The queue-first layout survived the
//   change unaltered, which is the argument for it: the page was built around
//   the state that matters, not around the one verb it happened to have.
//
// V3, which is this: lead with the STAGED QUEUE, because candidates waiting on a
// decision are the only state on this page that changes and the only one worth
// returning for. The input is one line. What the page can and cannot do is a
// single sentence at the top, said plainly, instead of a four-step strip.
// ─────────────────────────────────────────────────────────────────────────────

type Row = DiscoveredAccount & { id: string };

const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

function scoreAgainst(icp: IcpProfile | null, d: {
  employees: number | null; industry: string | null;
}): { score: number; reasons: string[] } {
  if (!icp) return { score: 0, reasons: ['No ICP defined — nothing to score against.'] };
  const reasons: string[] = [];
  let score = 50;

  if (icp.employee_min !== null || icp.employee_max !== null) {
    const n = d.employees;
    if (n === null) reasons.push('Headcount unknown');
    else if ((icp.employee_min ?? 0) <= n && n <= (icp.employee_max ?? Infinity)) {
      score += 25; reasons.push(`${n} people is inside your range`);
    } else {
      score -= 25; reasons.push(`${n} people is outside your range`);
    }
  }
  const inds = list(icp.industries);
  if (inds.length && d.industry) {
    const hit = inds.some((i) => d.industry!.toLowerCase().includes(i.toLowerCase()));
    if (hit) { score += 25; reasons.push(`Industry matches "${d.industry}"`); }
    else { score -= 15; reasons.push(`Industry "${d.industry}" is not on your list`); }
  }
  return { score: Math.max(0, Math.min(100, score)), reasons };
}

export function Research() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const icps = useRecords<IcpProfile & { id: string; is_default: boolean; pains: string | null }>('icp_profiles');
  const personas = useRecords<Persona & { id: string; titles: string[] }>('personas');
  const staged = useRecords<Row>('discovered_accounts', { status: 'new' });

  const [icpId, setIcpId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brief, setBrief] = useState<string | null>(null);
  // Slugs promoted in THIS sitting. The point of collecting them is the
  // handoff: the list builder wants the set you just added, and asking the
  // operator to re-select them on another screen is the click count this
  // whole change exists to remove.
  const [added, setAdded] = useState<string[]>([]);

  useEffect(() => {
    const i = params.get('icp');
    if (i) setIcpId(i);
    const p = params.get('personas');
    if (p) setPicked(new Set(p.split(',').filter(Boolean)));
  }, [params]);

  useEffect(() => {
    if (!icpId && icps.rows?.length) {
      setIcpId(icps.rows.find((x) => x.is_default)?.id ?? icps.rows[0].id);
    }
  }, [icps.rows, icpId]);

  const icp = icps.rows?.find((x) => x.id === icpId) ?? null;
  const chosen = useMemo(
    () => (personas.rows ?? []).filter((p) => picked.has(p.id)),
    [personas.rows, picked]);
  const titles = useMemo(
    () => [...new Set(chosen.flatMap((p) => list(p.titles)))], [chosen]);

  function buildBrief(): string {
    const L: string[] = ['RESEARCH BRIEF'];
    if (domain.trim()) L.push(`Company: ${domain.trim()}`);
    if (icp) {
      L.push('', `Looking for companies like: ${icp.name}`);
      if (icp.summary) L.push(icp.summary);
      if (list(icp.industries).length) L.push(`Industries: ${list(icp.industries).join(', ')}`);
      if (icp.employee_min || icp.employee_max) {
        L.push(`Size: ${icp.employee_min ?? '—'}–${icp.employee_max ?? '—'} employees`);
      }
      if (icp.pains) L.push(`What is broken inside them: ${icp.pains}`);
      const ex = list(icp.exclusions);
      if (ex.length) L.push(`Skip: ${ex.join('; ')}`);
    }
    if (chosen.length) {
      L.push('', 'Find these people:');
      for (const p of chosen) L.push(`- ${p.name} — ${list(p.titles).join(', ')}`);
    }
    L.push('', 'For each company return: name, domain, headcount, funding and ownership,',
      'and the named people matching those titles. Cite a source for every claim.');
    return L.join('\n');
  }

  async function stage() {
    const d = domain.trim().toLowerCase()
      .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
    if (!d) return;
    setBusy('stage'); setError(null);
    try {
      const sc = scoreAgainst(icp, { employees: null, industry: null });
      await staged.create({
        name: d, domain: d, icp_id: icpId || null, provider: 'manual',
        query: icp?.name ?? null, icp_score: sc.score, icp_reasons: sc.reasons,
        status: 'new',
      });
      setDomain('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  // PROMOTING NO LONGER NAVIGATES.
  //
  // It used to jump to the new account, which is right for one candidate and
  // wrong for the queue this page is built around: adding 25 of them meant 25
  // page loads and finding your way back each time. The added ones leave the
  // list, which is feedback enough — and `added` collects them for the builder.
  async function promote(d: Row): Promise<string | null> {
    setBusy(d.id); setError(null);
    try {
      // NO icp_score — derived, and the server 400s it. See Source.tsx; this
      // was the same never-worked promote in its original home.
      const company = await createRecord<{ id: string; slug: string }>('companies', {
        name: d.name, domain: d.domain, url: d.domain ? `https://${d.domain}` : null,
        icp_id: d.icp_id, lifecycle: 'target',
        stage: 'identified', source: `Research — ${d.query ?? d.provider}`,
      });
      await staged.update(d.id, { status: 'added', company_id: company.record.id });
      setAdded((prev) => [...prev, company.record.slug]);
      return company.record.slug;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      return null;
    } finally { setBusy(null); }
  }

  // Add every candidate on the list, in order. Sequential on purpose: the API
  // is one insert per call and firing 25 at once at Neon buys nothing but a
  // burst of connection failures on this link.
  async function promoteAll() {
    setBusy('all'); setError(null);
    for (const d of rows) {
      const done = await promote(d);
      if (!done) break;
    }
    setBusy(null);
    void staged.reload();
  }

  const rows = staged.rows ?? [];

  return (
    <div className="research">
      <header className="todayhead">
        <h1>Research</h1>
        {/* One honest sentence beats a four-step instruction strip. If a screen
            needs instructions, the screen is the problem. */}
        <p className="meta">
          Candidates waiting on a decision. Send an agent to go find them, add one
          you already know, or take a brief somewhere else and bring it back.
        </p>
      </header>

      <Err>{error}</Err>

      {/* THE QUEUE FIRST. Candidates waiting on a decision are the only state
          here that changes, and the only reason to come back to this page. */}
      {/* THE BATCH BAR. One instruction — "add these" — used to be one click per
          candidate plus a page load each. The handoff below it matters as much:
          having added ten accounts, the next thing you want is their people, and
          re-selecting the same ten on another screen is the cost this removes. */}
      {rows.length > 0 && (
        <div className="toolbar batchbar">
          <button disabled={busy !== null} onClick={() => void promoteAll()}>
            {busy === 'all' ? 'Adding…' : `Add all ${rows.length} to the CRM`}
          </button>
          {added.length > 0 && (
            <button className="primary" onClick={() => nav(`/build?slugs=${added.join(',')}`)}>
              Find the people at {added.length} account{added.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <ol className="queue" style={{ marginBottom: 22 }}>
          {rows.map((d) => (
            <li className="qitem" key={d.id}>
              <span className="qicon" aria-hidden="true">⌕</span>
              <div className="qbody">
                <div className="qline">
                  <strong>{d.name}</strong>
                  {d.icp_score !== null && (
                    <span className={`flag ${d.icp_score >= 70 ? 'flag-ok'
                      : d.icp_score >= 45 ? 'flag-warn' : 'flag-mute'}`}>
                      fit {d.icp_score}
                    </span>
                  )}
                  <span className="qmin">{ago(d.found_at)}</span>
                </div>
                <span className="qwhy">
                  {list(d.icp_reasons)[0] ?? `From ${d.query ?? d.provider}`}
                </span>
              </div>
              <button disabled={busy !== null}
                onClick={() => void promote(d)}>
                {busy === d.id ? 'Adding…' : 'Add to CRM'}
              </button>
              <button className="qsnooze" onClick={() => void staged.update(d.id, {
                status: 'dismissed', dismissed_reason: 'Not a fit',
              })}>Dismiss</button>
            </li>
          ))}
        </ol>
      )}

      {rows.length === 0 && (
        <Empty
          title="Nothing staged"
          hint="Candidates land here before they become accounts. Add a company below, or build a brief and go find some."
        />
      )}

      <AgentRun icpId={icpId || null} onFound={() => void staged.reload()} />

      {/* ONE LINE, NOT FOUR SECTIONS. */}
      <section className="panel">
        <h4>Add a company</h4>
        <div className="toolbar">
          <input className="search" value={domain} placeholder="payra.com"
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void stage(); }} />
          <select value={icpId} onChange={(e) => setIcpId(e.target.value)}>
            {icps.rows?.length === 0 && <option value="">No ICP yet</option>}
            {icps.rows?.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <button className="primary" disabled={busy === 'stage' || !domain.trim()}
            onClick={() => void stage()}>
            {busy === 'stage' ? 'Staging…' : 'Stage it'}
          </button>
        </div>
        <p className="meta">
          Scored against the ICP you pick, then it waits above for you to promote it.
        </p>
      </section>

      <section className="panel">
        <h4>Take a brief and go looking</h4>
        <p className="meta">
          The app has no search backend. This assembles what you know — the profile,
          its disqualifiers, and the titles from the personas you tick — into
          something you can run in the Copilot or a browser.
        </p>

        {(personas.rows?.length ?? 0) > 0 && (
          <div className="chips" style={{ margin: '12px 0' }}>
            {personas.rows
              ?.filter((p) => !p.icp_id || p.icp_id === icpId)
              .map((p) => (
                <button key={p.id} className={`chip ${picked.has(p.id) ? 'on' : ''}`}
                  onClick={() => setPicked((s) => {
                    const n = new Set(s); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n;
                  })}>{p.name}</button>
              ))}
          </div>
        )}

        {titles.length > 0 && (
          <p className="meta">{titles.length} titles: {titles.slice(0, 8).join(', ')}
            {titles.length > 8 ? '…' : ''}</p>
        )}

        <div className="actions">
          <button onClick={() => setBrief(buildBrief())}>Build the brief</button>
          {brief && (
            <button onClick={() => void navigator.clipboard.writeText(brief)}>Copy</button>
          )}
        </div>
        {brief && <pre className="emailbody">{brief}</pre>}
      </section>
    </div>
  );
}
